// SPDX-License-Identifier: MIT
// The leading entry deposit surface (guided first-run beat ①/②). The deposit box
// IS the opening act: write your idea OR drop / paste / link a whole folder, in
// one surface (operator correction 2026-06-23 — the box must lead, not be deferred).
//
// PRESENTATIONAL over the PROVEN drop-zone handlers: it owns no file logic. A
// hidden renderDropZone instance carries the validated file pipeline (pick /
// folder-pick / drag / document-paste) and its exposed hooks (__triggerFilePicker
// / __triggerFolderPicker / __addFiles); the visible affords + the box's own drop
// target call those hooks. Each accepted file fires onFile once and adds one chip.
// Faithful to /__mock/guided-first-run-v3/ (beat ①/②); styles in guided-first-run.css.
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import { renderDropZone, type DropZoneFile } from './drop-zone';

export interface DepositBoxOptions {
  // "Commencer →" / Cmd-Enter on the textarea → the typed idea (may be empty).
  onTextSubmit(text: string): void;
  // Fires once per accepted file (delegated from the drop-zone's onAdd).
  onFile?(file: File): void;
  // Fires when a piece chip is removed.
  onRemoveFile?(file: File): void;
  // Test injection, forwarded to the drop-zone.
  uuid?: () => string;
}

type DropZoneHooks = HTMLElement & {
  __triggerFilePicker?: () => void;
  __triggerFolderPicker?: () => void;
  __addFiles?: (files: File[]) => void;
};

export function renderDepositBox(opts: DepositBoxOptions): HTMLElement {
  const root = document.createElement('div');
  root.className = 'dropbox';
  root.setAttribute('data-component', 'deposit-box');
  root.innerHTML = `
    <div class="dropbox__head">
      <div class="dropbox__glyph" aria-hidden="true">⤓</div>
      <div class="dropbox__titles">
        <div class="dropbox__title">${escapeHtml(t('onboarding.deposit.title'))}</div>
        <div class="dropbox__sub">${escapeHtml(t('onboarding.deposit.sub'))}</div>
      </div>
    </div>
    <span class="dropbox__or">${escapeHtml(t('onboarding.deposit.or'))}</span>
    <textarea class="dropbox__write" data-component="deposit-write" rows="2"
      placeholder="${escapeHtml(t('onboarding.deposit.placeholder'))}"
      aria-label="${escapeHtml(t('onboarding.deposit.placeholder'))}"></textarea>
    <div class="dropbox__hint">${escapeHtml(t('onboarding.deposit.hint'))}</div>
    <ul class="pieces" data-component="deposit-pieces" aria-live="polite" hidden></ul>
    <div class="dropbox__affords">
      <button type="button" class="afford" data-component="deposit-glisse">${escapeHtml(t('onboarding.deposit.glisse'))}</button>
      <span class="afford" data-component="deposit-colle" aria-hidden="true">${escapeHtml(t('onboarding.deposit.colle'))}</span>
      <button type="button" class="afford afford--folder" data-component="deposit-folder">${escapeHtml(t('onboarding.deposit.folder'))}</button>
      <button type="button" class="afford afford--go" data-component="deposit-go">${escapeHtml(t('onboarding.deposit.go'))}</button>
    </div>
  `;

  const pieces = root.querySelector<HTMLUListElement>('[data-component="deposit-pieces"]')!;
  const write = root.querySelector<HTMLTextAreaElement>('[data-component="deposit-write"]')!;

  // Hidden drop-zone — owns the file machinery. Each accepted file → onFile + chip.
  const dropZone = renderDropZone({
    uuid: opts.uuid,
    onAdd: (entry) => {
      opts.onFile?.(entry.file);
      appendChip(entry);
    },
  }) as DropZoneHooks;
  const dzHost = document.createElement('div');
  dzHost.hidden = true;
  dzHost.setAttribute('data-component', 'deposit-dropzone-host');
  dzHost.appendChild(dropZone);
  root.appendChild(dzHost);
  (root as unknown as { __dropZone: DropZoneHooks }).__dropZone = dropZone;

  function appendChip(entry: DropZoneFile): void {
    pieces.hidden = false;
    const li = document.createElement('li');
    li.className = 'piece';
    li.setAttribute('data-component', 'deposit-piece');
    li.setAttribute('data-file-id', entry.id);
    li.innerHTML = `
      <span class="piece__ic" aria-hidden="true">${iconFor(entry.file)}</span>
      <span class="piece__nm">${escapeHtml(entry.file.name)}</span>
      <span class="piece__sz">${formatBytes(entry.file.size)}</span>
      <button type="button" class="piece__x" aria-label="${escapeHtml(t('onboarding.deposit.removeAria'))}">×</button>
    `;
    li.querySelector<HTMLButtonElement>('.piece__x')!.addEventListener('click', () => {
      li.remove();
      if (pieces.children.length === 0) pieces.hidden = true;
      opts.onRemoveFile?.(entry.file);
    });
    pieces.appendChild(li);
  }

  // Affords → the proven drop-zone hooks (accessed at click time so they stay
  // overridable in tests and resilient if the drop-zone re-renders).
  root.querySelector<HTMLButtonElement>('[data-component="deposit-glisse"]')!
    .addEventListener('click', () => dropZone.__triggerFilePicker?.());
  root.querySelector<HTMLButtonElement>('[data-component="deposit-folder"]')!
    .addEventListener('click', () => dropZone.__triggerFolderPicker?.());

  const submit = (): void => opts.onTextSubmit(write.value.trim());
  root.querySelector<HTMLButtonElement>('[data-component="deposit-go"]')!
    .addEventListener('click', submit);
  // Cmd/Ctrl+Enter submits; plain Enter keeps adding lines to the idea.
  write.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  });

  // The visible box is itself a drop target → same validated pipeline.
  root.addEventListener('dragover', (e) => { e.preventDefault(); root.setAttribute('data-state', 'drag-over'); });
  root.addEventListener('dragleave', (e) => {
    if (!root.contains(e.relatedTarget as Node | null)) root.removeAttribute('data-state');
  });
  root.addEventListener('drop', (e) => {
    e.preventDefault();
    root.removeAttribute('data-state');
    const dt = (e as DragEvent).dataTransfer;
    if (dt) dropZone.__addFiles?.(Array.from(dt.files));
  });

  return root;
}

function iconFor(file: File): string {
  const n = (file.name || '').toLowerCase();
  if (/\.(png|jpe?g|gif|webp)$/.test(n) || file.type.startsWith('image/')) return '🖼️';
  if (n.endsWith('.pdf') || file.type === 'application/pdf') return '📄';
  return '📝';
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
